import { Completion } from '@codemirror/autocomplete'
import { InfoRounded, NotListedLocationRounded } from '@mui/icons-material'
import { Box, Button, CircularProgress, MenuItem, Skeleton, Stack, TextField, Typography } from '@mui/material'
import { ChangeEvent, memo, useEffect, useMemo, useRef, useState } from 'react'
import ApiClient from '../../../../../backend/ApiClient'
import { IFlow, INode } from '../../../../../definitions/Nodes'
import { IAcceptedData } from '../../../../../definitions/NodesDescription'
import NodeManager from '../../../../../managers/NodeManager'
import { useAppDispatch } from '../../../../../store/Hooks'
import { changeRoute } from '../../../../../store/slices/SessionSlice'
import useCommand from '../../../../hooks/useCommand'
import useQuery, { buildQueryKey } from '../../../../hooks/useQuery'
import { snackbar } from '../../../../services/SnackbarService'
import Placeholders from './Placeholders'

interface P {
    acceptedData: IAcceptedData<'out_report'>
    selectedNode: INode<'out_report'>
    flow: IFlow
    setSelected: React.Dispatch<React.SetStateAction<INode<'out_report'>>>
    disable: boolean
}

export function useDebouncedCommit<T>(initial: T, commit: (value: T) => void, delay = 500) {
    const [value, setValue] = useState(initial)
    const timer = useRef<NodeJS.Timeout>()

    const update = (next: T) => {
        setValue(next)
        if (timer.current) clearTimeout(timer.current)
        timer.current = setTimeout(() => commit(next), delay)
    }

    useEffect(() => () => timer.current && clearTimeout(timer.current), [])

    return [value, update] as const
}

const NodeReportEditor = ({ selectedNode, flow, setSelected, disable }: P) => {
    const [contents, setContents] = useState<{ name: string, contents: string[] }[]>([])

    const dispatch = useAppDispatch()

    const { status: getReportsStatus, response: getReports } = useQuery({
        queryKey: buildQueryKey('reports'),
        queryFn: () => ApiClient.getReports(),
        cache: true
    })

    const { execute: getPlaceholders, status: getPlaceholdersStatus } = useCommand({
        commandFn: (uuid: string) => ApiClient.generateReportPlaceholders(uuid),
        onFinish: result => {
            if (result.status === 'success') {
                const mappedReport = NodeManager.mapReport(result.response, selectedNode.data.reportData?.placeholders)
                setContents(mappedReport.contents)
                setSelected(prev => ({
                    ...prev,
                    data: {
                        ...prev.data,
                        reportData: {
                            ...prev.data.reportData,
                            placeholders: mappedReport.placeholders
                        }
                    }
                }))
            }
            if (result.status === 'error') {
                snackbar.show({ text: result.errorText, severity: 'error' })
            }
        }
    })

    const commitReportName = (val: string) => {
        setSelected(prev => ({
            ...prev,
            data: {
                ...prev.data,
                reportData: { ...prev.data.reportData, reportName: val }
            }
        }))
    }

    const [reportName, setReportName] = useDebouncedCommit(
        selectedNode.data.reportData?.reportName ?? '',
        commitReportName,
        500
    )

    const handleReportChange = (e: ChangeEvent<HTMLInputElement>) => {
        setSelected(prev => ({
            ...prev,
            data: {
                ...prev.data,
                reportData: {
                    ...prev.data.reportData,
                    reportUUID: e.target.value
                }
            }
        }))
        getPlaceholders(e.target.value)
    }

    useEffect(() => {
        if (selectedNode.data.reportData?.reportUUID) {
            getPlaceholders(selectedNode.data.reportData.reportUUID)
        }
    }, [selectedNode.data.reportData?.reportUUID])

    const reports = useMemo(
        () =>
            getReportsStatus === 'success'
                ? getReports.map(r => ({ uuid: r.uuid, name: r.name }))
                : [],
        [getReports, getReportsStatus]
    )

    const textPlaceholders = useMemo(() => {
        const allPlaceholders = selectedNode.data.reportData?.placeholders ?? []
        return allPlaceholders
            .map((p, i) => ({ index: i, placeholder: p }))
            .filter(({ placeholder }) => !placeholder.columns)
    }, [selectedNode.data.reportData?.placeholders])

    const datasetPlaceholders = useMemo(() => {
        const allPlaceholders = selectedNode.data.reportData?.placeholders ?? []
        return allPlaceholders
            .map((p, i) => ({ index: i, placeholder: p }))
            .filter(({ placeholder }) => !!placeholder.columns)
    }, [selectedNode.data.reportData?.placeholders])

    const memoizeContents = useMemo(() => contents, [contents])

    const loading = getReportsStatus === 'loading' || getPlaceholdersStatus === 'loading'

    const shapes = useMemo(() => NodeManager.getInputShapes<'out_report'>(selectedNode, flow.nodes), [selectedNode, flow])

    const globalsSuggestions: Completion[] = useMemo(() => {
        const globalsDataShape = flow?.globals ? Object.keys(flow.globals) : []
        return globalsDataShape.length > 0
            ? globalsDataShape.map(x => ({
                label: `globals.${x}`,
                type: 'fieldInput',
                info: '-> Field Input'
            }))
            : []
    }, [flow])

    return (
        <Stack height='100%' flex={1} spacing={4}>
            {/* Report name */}
            <Stack spacing={1}>
                <Typography variant='subtitle2'>Report name</Typography>
                <Stack flexDirection='row' gap={1}>
                    <TextField
                        size='small'
                        fullWidth
                        disabled={disable || loading}
                        value={reportName}
                        placeholder='Write the name of generated report...'
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setReportName(e.target.value)}
                    />
                    <NotListedLocationRounded fontSize='small' sx={{ visibility: 'hidden' }} />
                </Stack>
            </Stack>

            {/* Template selection */}
            <Stack spacing={1}>
                <Typography variant='subtitle2'>Select template</Typography>
                <Stack flexDirection='row' gap={1}>
                    <TextField
                        size='small'
                        fullWidth
                        disabled={disable || loading}
                        value={selectedNode.data.reportData?.reportUUID ?? ''}
                        select
                        InputProps={{
                            endAdornment: loading
                                ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                pr: 'calc(var(--unit) * 3)'
                                            }}>
                                            <CircularProgress color='inherit' size={18} />
                                        </Box>
                                    )
                                : null
                        }}
                        onChange={handleReportChange}>
                        {reports.map(report => (
                            <MenuItem key={report.uuid} value={report.uuid}>
                                {report.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <NotListedLocationRounded fontSize='small' sx={{ visibility: 'hidden' }} />
                </Stack>
                <Stack flexDirection='row' gap={1}>
                    <Button
                        fullWidth
                        size='small'
                        disabled={loading || !selectedNode.data.reportData?.reportUUID}
                        variant='text'
                        onClick={() => {
                            if (getReportsStatus === 'success') {
                                dispatch(
                                    changeRoute({
                                        route: 'report-editor',
                                        params: {
                                            report: getReports.find(r => r.uuid === selectedNode.data.reportData?.reportUUID)
                                        }
                                    })
                                )
                            }
                        }}>
                        Go to template
                    </Button>
                    <NotListedLocationRounded fontSize='small' sx={{ visibility: 'hidden' }} />
                </Stack>
            </Stack>

            {/* Placeholders */}
            {loading
                ? (
                        <>
                            {Array.from({ length: 4 }).map((_, i) => (
                                <Stack spacing={1} key={i}>
                                    <Stack flexDirection='row' gap={1}>
                                        <Skeleton variant='text' sx={{ flex: 1, fontSize: 13.5 }} />
                                        <InfoRounded fontSize='small' sx={{ visibility: 'hidden' }} />
                                    </Stack>
                                </Stack>
                            ))}
                        </>
                    )
                : (
                        <Placeholders
                            contents={memoizeContents}
                            textPlaceholders={textPlaceholders}
                            datasetPlaceholders={datasetPlaceholders}
                            setSelected={setSelected}
                            disable={disable}
                            shapes={shapes}
                            globalsSuggestions={globalsSuggestions}
                        />
                    )}
        </Stack>
    )
}

export default memo(NodeReportEditor)
